const zeros = (count: number): string => '0'.repeat(count)

export const excelNumFmt = (decimals: number): string =>
  decimals > 0 ? `#,##0.${zeros(decimals)}` : '#,##0'

export const excelEuroFmt = (decimals: number): string => `${excelNumFmt(decimals)} "€"`

export const excelPercentFmt = (decimals: number): string =>
  decimals > 0 ? `0.${zeros(decimals)} %` : '0 %'

const EXCEL_SHEET_NAME_MAX_LENGTH = 31
const EXCEL_SHEET_NAME_FORBIDDEN = /[[\]:*?/\\]/g

export const excelSheetName = (name: string): string =>
  name.replace(EXCEL_SHEET_NAME_FORBIDDEN, ' ').slice(0, EXCEL_SHEET_NAME_MAX_LENGTH).trim()

export type ExportValueKind = 'text' | 'number' | 'cents' | 'percent' | 'approx' | 'date'

export interface ExportColumn {
  header: string
  numFmt?: string
  width?: number
  kind?: ExportValueKind
}

export type ExportColumnMap<T> = Record<keyof T, ExportColumn | null>

export type ExportCellValue = string | number | Date | null

export interface ExportField<T> {
  key: keyof T
  column: ExportColumn
}

export const APPROX_LABEL = 'approx.'

export const exportFields = <T>(map: ExportColumnMap<T>): ExportField<T>[] =>
  (Object.keys(map) as (keyof T)[])
    .map((key) => ({ key, column: map[key] }))
    .filter((field): field is ExportField<T> => field.column !== null)

export const exportHeaders = <T>(map: ExportColumnMap<T>): string[] =>
  exportFields(map).map((field) => field.column.header)

export const exportWidths = <T>(map: ExportColumnMap<T>): (number | undefined)[] =>
  exportFields(map).map((field) => field.column.width)

const utcDateFromIso = (iso: string): Date => new Date(`${iso}T00:00:00Z`)

const kindOf = (column: ExportColumn): ExportValueKind =>
  column.kind ?? (column.numFmt === undefined ? 'text' : 'number')

export const exportCellValue = (column: ExportColumn, value: unknown): ExportCellValue => {
  const kind = kindOf(column)
  if (kind === 'approx') return value === true ? APPROX_LABEL : null
  if (kind === 'date') return typeof value === 'string' ? utcDateFromIso(value) : null
  if (kind === 'text') return value === null || value === undefined ? null : String(value)
  const numeric = typeof value === 'number' ? value : 0
  if (kind === 'cents') return numeric / 100
  if (kind === 'percent') return Number((numeric / 100).toPrecision(15))
  return numeric
}

export const exportRowValues = <T>(map: ExportColumnMap<T>, row: T): ExportCellValue[] =>
  exportFields(map).map((field) => exportCellValue(field.column, row[field.key]))
